import { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { env } from 'utils'
import { z } from 'zod'

import { axiosClient } from '../api/apiClient'

const queryKey = 'userTips'

const zTipsReadData = z.object({
    tips: z.object({
        sent: z.record(z.string()).optional().default({}),
        received: z.record(z.string()).optional().default({}),
        sentCount: z.record(z.string()).optional().default({}),
        receivedCount: z.record(z.string()).optional().default({}),
    }),
})

async function getUserTips(userId: string) {
    if (!userId) {
        return {
            sent: {},
            received: {},
            sentCount: {},
            receivedCount: {},
        }
    }

    try {
        const metadataUrl = new URL(env.VITE_RIVER_STREAM_METADATA_URL)
        metadataUrl.pathname = `/user/${userId}/tips`
        const userTipsResponse = await axiosClient.get(metadataUrl.toString())
        const parseResult = zTipsReadData.safeParse(userTipsResponse.data)

        if (!parseResult.success) {
            console.error("Couldn't parse user tips:", parseResult.error)
            return {
                sent: {},
                received: {},
                sentCount: {},
                receivedCount: {},
            }
        }

        return {
            sent: parseResult.data.tips.sent,
            received: parseResult.data.tips.received,
            sentCount: parseResult.data.tips.sentCount,
            receivedCount: parseResult.data.tips.receivedCount,
        }
    } catch (error) {
        console.error('Failed to fetch user tips:', error)
        return {
            sent: {},
            received: {},
            sentCount: {},
            receivedCount: {},
        }
    }
}

export const useUserTips = (userId: string | undefined) => {
    const _getUserTips = useCallback(async () => {
        if (!userId) {
            return {
                sent: {},
                received: {},
                sentCount: {},
                receivedCount: {},
            }
        }
        return await getUserTips(userId)
    }, [userId])

    return useQuery({
        queryKey: [queryKey, userId],
        queryFn: _getUserTips,
        enabled: !!userId,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 1000 * 60 * 60 * 1, // 1 hour
    })
}
